import { useGlobalContext } from "context/GlobalContext";
import { createContext, useContext, useEffect, useState } from "react";
import { message } from "constants/responses/message";
import { projectOperationsList } from "./constants/project.constants";
import { fetchProjectsApi } from "API/apis/project/getProjects";
import { useSearchParams } from "react-router-dom";
import { filterStringToObject } from "utils/functions/reactTableFilterConverter";

const ProjectContext = createContext({
  currentOperationPage: "",
  setCurrentOperationPage: () => {},
  list: [],
  setList: () => {},
  previewProject: {},
  setPreviewProject: () => {},
  reloadRequired: false,
  setReloadRequired: () => {},
});

export const ProjectProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const paramsToObject = Object.fromEntries(searchParams.entries());

  const [list, setList] = useState([]);
  const [previewProject, setPreviewProject] = useState({});
  const [reloadRequired, setReloadRequired] = useState(false);
  const { pageAccess } = useGlobalContext();
  const [currentOperationPage, setCurrentOperationPage] = useState(
    projectOperationsList.listProject
  );

  const { setIsInitializing, setError, setIsLoading } = useGlobalContext();

  useEffect(() => {
    setAllParams({
      ...paramsToObject,
      filters: filterStringToObject(paramsToObject.filters),
    });
  }, [searchParams]);

  const [allParams, setAllParams] = useState({
    ...paramsToObject,
    filters: filterStringToObject(paramsToObject.filters) || "",
  });

  useEffect(() => {
    setIsLoading(true);
    const fetchData = async () => {
      if (pageAccess) {
        try {
          setIsInitializing(true);
          const { data, status } = await fetchProjectsApi({
            canViewAll: pageAccess?.canViewAll || true,
            ...allParams,
          });
          if (status !== 200) {
            throw new Error(message.error.generalFailure);
          }
          setList(data);
        } catch (error) {
          setError(error.message);
        } finally {
          setIsInitializing(false);
          setIsLoading(false);
          setReloadRequired(false);
        }
      }
    };
    fetchData();
  }, [reloadRequired, allParams]);

  return (
    <ProjectContext.Provider
      value={{
        currentOperationPage,
        setCurrentOperationPage,
        list,
        setList,
        previewProject,
        setPreviewProject,
        reloadRequired,
        setReloadRequired,
      }}
    >
      <>{children}</>
    </ProjectContext.Provider>
  );
};

export const useProjectContext = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error("useProjectContext must be used within a ProjectProvider");
  }
  return context;
};
